import { useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { type ComponentProps, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Check } from "@/components/animate-ui/icons/check";
import { UsageWidget } from "@/components/billing/usage-widget";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { authClient } from "@/lib/api/auth-client";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";

type BillingVariant = "dashboard" | "public";
type BillingPeriod = "monthly" | "annual";
type PlanId = "free" | "pro" | "max";
type MaxUsageOption = keyof typeof MAX_USAGE_OPTIONS;
type ButtonVariant = ComponentProps<typeof Button>["variant"];

const ANNUAL_BILLING_MULTIPLIER = 10;

const MAX_USAGE_OPTIONS = {
	"5x": {
		label: "5× usage",
		runs: 500,
		hours: 250,
		tasks: 50,
		monthlyPrice: 99,
	},
	"20x": {
		label: "20× usage",
		runs: 2000,
		hours: 1000,
		tasks: 200,
		monthlyPrice: 299,
	},
} as const;

interface BasePlan {
	id: PlanId;
	name: string;
	description: string;
	monthlyPrice: number;
	badge?: string;
	features: string[];
}

function baseFeatureList({ runs }: { runs: number }) {
	return [`${runs.toLocaleString()} tasks per month`];
}

const BASE_PLANS: BasePlan[] = [
	{
		id: "free",
		name: "Free",
		description: "Get started free.",
		monthlyPrice: 0,
		badge: "Free forever",
		features: [...baseFeatureList({ runs: 5 }), "Community support"],
	},
	{
		id: "pro",
		name: "Pro",
		description: "Recurring tasks handled. Reports, research, and real actions.",
		monthlyPrice: 29,
		badge: "Most popular",
		features: [...baseFeatureList({ runs: 100 }), "Standard support"],
	},
	{
		id: "max",
		name: "Max",
		description: "High volume for teams and agencies.",
		monthlyPrice: MAX_USAGE_OPTIONS["5x"].monthlyPrice,
		badge: "Scale",
		features: [],
	},
];

const PLAN_RANK: Record<PlanId, number> = { free: 0, pro: 1, max: 2 };

function getUserPlanId(userPlan: string | undefined): PlanId {
	if (userPlan?.startsWith("max")) return "max";
	if (userPlan === "pro") return "pro";
	return "free";
}

interface PlanCtaConfig {
	label: string;
	variant?: ButtonVariant;
	to?: string;
}

const PUBLIC_CTA_CONFIG: Record<PlanId, PlanCtaConfig> = {
	free: { label: "Get started free", to: "/signup", variant: "default" },
	pro: { label: "Start Pro", to: "/signup", variant: "outline" },
	max: { label: "Upgrade to Max", to: "/login", variant: "outline" },
};

interface BillingPlansProps {
	variant: BillingVariant;
	className?: string;
}

export function BillingPlans({ variant, className }: BillingPlansProps) {
	const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
	const [maxUsage, setMaxUsage] = useState<MaxUsageOption>("5x");
	const [isLoading, setIsLoading] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const user = useAuthStore((state) => state.user);
	const setUser = useAuthStore((state) => state.setUser);
	const searchParams = useSearch({ strict: false });
	const queryClient = useQueryClient();
	const cancelledRef = useRef(false);

	// Capture plan at mount time so polling can compare without re-triggering the effect
	const planRef = useRef(user?.plan ?? "free");
	planRef.current = user?.plan ?? "free";

	// Refresh user state when returning from Stripe checkout
	useEffect(() => {
		const params = searchParams as Record<string, unknown>;
		if (params?.success !== "true" || variant !== "dashboard") return;

		window.history.replaceState({}, "", "/billing");
		cancelledRef.current = false;
		setIsRefreshing(true);

		const currentPlan = planRef.current;

		const refresh = async () => {
			for (let i = 0; i < 5; i++) {
				if (cancelledRef.current) return;
				if (i > 0) await new Promise((r) => setTimeout(r, 1500));
				if (cancelledRef.current) return;

				const { data } = await authClient.GET("/api/v1/auth/me");
				if (!data) continue;

				if (data.plan !== currentPlan) {
					setUser(data);
					await queryClient.invalidateQueries({ queryKey: ["billing", "usage"] });
					setIsRefreshing(false);
					toast.success("Upgrade complete. Your subscription is active.");
					return;
				}
			}
			// Webhook hasn't arrived yet — update what we can and let next API call catch it
			await queryClient.invalidateQueries({ queryKey: ["billing", "usage"] });
			setIsRefreshing(false);
			toast.success("Your upgrade is being processed. It should appear shortly.");
		};

		refresh();
		return () => {
			cancelledRef.current = true;
		};
	}, [searchParams, variant, setUser, queryClient]);

	const handleUpgrade = async (planId: string, period: BillingPeriod) => {
		try {
			setIsLoading(true);
			const { data, error } = await authClient.POST("/api/v1/billing/checkout", {
				body: {
					plan_id: planId,
					billing_period: period,
				},
			});

			if (error || !data) {
				const errorMsg =
					(error as { detail?: string })?.detail || "Couldn't create checkout session. Try again.";
				toast.error(errorMsg);
				return;
			}

			// Redirect to Stripe Checkout
			window.location.href = data.url;
		} catch (_error) {
			toast.error("Unexpected error creating checkout session. Try again.");
		} finally {
			setIsLoading(false);
		}
	};

	const handleManageSubscription = async () => {
		try {
			setIsLoading(true);
			const { data, error } = await authClient.POST("/api/v1/billing/portal");

			if (error || !data) {
				const errorMsg =
					(error as { detail?: string })?.detail ||
					"Couldn't open subscription management. Try again.";
				toast.error(errorMsg);
				return;
			}

			// Redirect to Stripe Customer Portal
			window.location.href = data.url;
		} catch (_error) {
			toast.error("Unexpected error opening subscription management. Try again.");
		} finally {
			setIsLoading(false);
		}
	};

	const userPlanId = getUserPlanId(user?.plan);

	const getPlanButtonText = (plan: BasePlan): string => {
		if (variant !== "dashboard") return PUBLIC_CTA_CONFIG[plan.id].label;

		if (plan.id === userPlanId) {
			return userPlanId === "free" ? "Current Plan" : "Manage Subscription";
		}
		if (PLAN_RANK[plan.id] < PLAN_RANK[userPlanId]) return "Downgrade";
		return `Upgrade to ${plan.name}`;
	};

	const isPlanDisabled = (plan: BasePlan): boolean =>
		variant === "dashboard" && plan.id === "free" && userPlanId === "free";

	const handlePlanClick = (plan: BasePlan) => {
		if (variant !== "dashboard") return;
		if (isPlanDisabled(plan)) return;

		// Current plan or downgrade → Stripe portal
		if (PLAN_RANK[plan.id] <= PLAN_RANK[userPlanId]) {
			handleManageSubscription();
			return;
		}

		// Upgrade
		if (plan.id === "pro") {
			handleUpgrade("pro", billingPeriod);
		} else if (plan.id === "max") {
			handleUpgrade(`max_${maxUsage}`, billingPeriod);
		}
	};

	const plans = useMemo(() => {
		return BASE_PLANS.map((plan) => {
			if (plan.id !== "max") {
				return plan;
			}

			const selectedMax = MAX_USAGE_OPTIONS[maxUsage];
			return {
				...plan,
				monthlyPrice: selectedMax.monthlyPrice,
				description: "High volume for teams and agencies.",
				features: [...baseFeatureList({ runs: selectedMax.runs }), "Priority support"],
			};
		});
	}, [maxUsage]);

	const highlightedPlanId: PlanId = variant === "public" ? "free" : "pro";

	if (isRefreshing) {
		return (
			<div className={cn("container mx-auto w-full max-w-6xl px-4", className)}>
				<div className="flex flex-col items-center justify-center gap-3 py-32">
					<Spinner className="size-8" />
					<p className="text-sm text-muted-foreground">Activating your subscription...</p>
				</div>
			</div>
		);
	}

	return (
		<div className={cn("container mx-auto w-full max-w-6xl px-4", className)}>
			<div className="flex flex-col gap-8 py-6">
				<header className="text-center md:text-left">
					<div className="flex items-center justify-center gap-3 md:justify-start">
						<h1 className="text-3xl font-semibold tracking-tight md:text-4xl">Pricing</h1>
						{variant === "dashboard" && user?.plan && (
							<Badge variant="secondary" className="text-sm">
								{user.plan === "free"
									? "Free"
									: user.plan === "pro"
										? "Pro"
										: user.plan === "max_5x"
											? "Max 5x"
											: user.plan === "max_20x"
												? "Max 20x"
												: user.plan}
							</Badge>
						)}
					</div>
					<p className="mt-2 text-muted-foreground">
						Simple pricing. Start free, upgrade when you need more.
					</p>
				</header>

				{variant === "dashboard" && <UsageWidget variant="card" />}

				<div className="flex items-center justify-center gap-3 text-sm font-medium">
					<span>Monthly</span>
					<Switch
						checked={billingPeriod === "annual"}
						onCheckedChange={(checked) => setBillingPeriod(checked ? "annual" : "monthly")}
						aria-label="Toggle annual billing"
					/>
					<span>Annual</span>
				</div>

				<section className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
					{plans.map((plan) => {
						const isHighlighted = plan.id === highlightedPlanId;
						const publicCta = variant === "public" ? PUBLIC_CTA_CONFIG[plan.id] : null;
						const isUpgrade = variant === "dashboard" && PLAN_RANK[plan.id] > PLAN_RANK[userPlanId];
						const buttonVariant =
							publicCta?.variant ?? (isUpgrade && isHighlighted ? "default" : "outline");

						return (
							<Card
								key={plan.id}
								className={cn(
									"flex h-full flex-col border-border/70",
									isHighlighted &&
										"border-primary/60 shadow-lg shadow-primary/5 ring-1 ring-primary/20"
								)}
							>
								<CardHeader className="space-y-3">
									<div className="flex items-center justify-between gap-2">
										<CardTitle className="text-2xl font-semibold">{plan.name}</CardTitle>
										{plan.badge ? (
											<Badge variant={isHighlighted ? "default" : "secondary"}>{plan.badge}</Badge>
										) : null}
									</div>
									<CardDescription>{plan.description}</CardDescription>

									<div className="pt-2">
										<PlanPrice plan={plan} billingPeriod={billingPeriod} />
									</div>
								</CardHeader>

								<CardContent className="flex flex-1 flex-col gap-4">
									{plan.id === "max" ? (
										<ToggleGroup
											type="single"
											size="sm"
											value={maxUsage}
											className="w-full"
											onValueChange={(value) => {
												if (!value) return;
												setMaxUsage(value as MaxUsageOption);
											}}
										>
											{(Object.keys(MAX_USAGE_OPTIONS) as MaxUsageOption[]).map((option) => (
												<ToggleGroupItem key={option} value={option} className="text-xs">
													{option.toUpperCase()}
												</ToggleGroupItem>
											))}
										</ToggleGroup>
									) : null}

									<Separator />

									<ul className="space-y-3 text-sm">
										{plan.features.map((feature) => (
											<li key={feature} className="flex items-start gap-2">
												<Check className="mt-0.5 h-4 w-4 text-primary" />
												<span>{feature}</span>
											</li>
										))}
									</ul>
								</CardContent>

								<CardFooter>
									{publicCta?.to ? (
										<Button className="w-full" variant={buttonVariant} asChild>
											<Link to={publicCta.to}>{publicCta.label}</Link>
										</Button>
									) : (
										<Button
											className="w-full"
											variant={buttonVariant}
											disabled={isLoading || isPlanDisabled(plan)}
											onClick={() => handlePlanClick(plan)}
										>
											{isLoading ? <Spinner className="size-4" /> : getPlanButtonText(plan)}
										</Button>
									)}
								</CardFooter>
							</Card>
						);
					})}
				</section>

				<Card className="border-dashed">
					<CardHeader className="gap-2">
						<CardTitle className="text-2xl">Enterprise</CardTitle>
						<CardDescription className="text-base">
							Hands-on onboarding, security, and custom limits.
						</CardDescription>
					</CardHeader>
					<CardContent className="grid gap-4 text-sm md:grid-cols-2">
						<ul className="space-y-3">
							<li className="flex items-start gap-2">
								<Check className="mt-0.5 h-4 w-4 text-primary" />
								Volume discounts & unlimited seats
							</li>
							<li className="flex items-start gap-2">
								<Check className="mt-0.5 h-4 w-4 text-primary" />
								Dedicated onboarding and setup
							</li>
							<li className="flex items-start gap-2">
								<Check className="mt-0.5 h-4 w-4 text-primary" />
								Private Slack support
							</li>
						</ul>
						<ul className="space-y-3">
							<li className="flex items-start gap-2">
								<Check className="mt-0.5 h-4 w-4 text-primary" />
								Custom integrations & usage limits
							</li>
							<li className="flex items-start gap-2">
								<Check className="mt-0.5 h-4 w-4 text-primary" />
								Audit logs, SSO, and SLAs
							</li>
							<li className="flex items-start gap-2">
								<Check className="mt-0.5 h-4 w-4 text-primary" />
								Dedicated success manager
							</li>
						</ul>
					</CardContent>
					<CardFooter className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
						<div className="text-left">
							<p className="text-3xl font-semibold">Custom</p>
							<p className="text-sm text-muted-foreground">
								Pricing tailored to your automation goals.
							</p>
						</div>
						{renderEnterpriseButton(variant)}
					</CardFooter>
				</Card>

				<p className="text-center text-xs text-muted-foreground">
					<span className="font-semibold">*</span> Usage limits apply. Prices exclude tax.
				</p>
			</div>
		</div>
	);
}

function renderEnterpriseButton(_variant: BillingVariant) {
	return (
		<Button size="lg" className="w-full md:w-auto" variant="default" asChild>
			<a href="mailto:sales@m8tes.ai">Contact sales</a>
		</Button>
	);
}

interface PlanPriceProps {
	plan: BasePlan;
	billingPeriod: BillingPeriod;
}

function PlanPrice({ plan, billingPeriod }: PlanPriceProps) {
	if (plan.monthlyPrice === 0) {
		return (
			<div>
				<p className="text-4xl font-semibold">Free</p>
				<p className="text-xs text-muted-foreground">Free forever</p>
			</div>
		);
	}

	const displayAmount =
		plan.monthlyPrice * (billingPeriod === "annual" ? ANNUAL_BILLING_MULTIPLIER : 1);

	const formatted = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 0,
	}).format(displayAmount);

	const billingSuffix = billingPeriod === "annual" ? "/yr" : "/mo";

	return (
		<div className="space-y-1">
			<div className="flex items-end gap-1 text-4xl font-semibold">
				<span>{formatted}</span>
				<span className="text-xl text-muted-foreground">{billingSuffix}*</span>
			</div>
			<p className="text-xs text-muted-foreground">
				{billingPeriod === "annual" ? `Billed annually` : "Billed monthly"}
			</p>
		</div>
	);
}
